import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { Alert } from '@material-ui/lab';
import type { ValkyrieLambda } from 'config-editor-common';
import {
  ValkyrieLambdaListEditor,
  getQueuesReferencedByLambdas,
  getValkyrieRemovedQueues,
} from 'config-editor';
import { useMemo } from 'react';
import { useRecordField } from '../fieldState';

interface UiOptions {
  defaultsFieldName?: string;
}

type Props = FieldExtensionComponentProps<Record<string, ValkyrieLambda>, UiOptions>;

export function ValkyrieLambdaEditor(props: Props) {
  const { items, defaults, rootFormData, commit } = useRecordField<ValkyrieLambda>(props);

  const queueNames = useMemo(() => {
    const queues = rootFormData?.sqs_queues;
    if (!Array.isArray(queues)) return [];
    return queues.map((q: { name?: string }) => q?.name).filter((n): n is string => Boolean(n));
  }, [rootFormData]);

  const removedQueues = useMemo(() => {
    const consumerLambdas = (rootFormData?.queue_consumer_lambdas ?? {}) as Record<
      string,
      ValkyrieLambda
    >;
    return getValkyrieRemovedQueues(
      getQueuesReferencedByLambdas(items, consumerLambdas),
      queueNames,
    );
  }, [items, queueNames, rootFormData?.queue_consumer_lambdas]);

  return (
    <>
      {removedQueues.length > 0 && (
        <Alert severity="warning" style={{ marginBottom: 16 }}>
          The following queue(s) are referenced by lambdas but have been removed from SQS Queues:{' '}
          <strong>{removedQueues.join(', ')}</strong>. Update or remove these references in the
          lambdas below, or re-add the queues in the SQS Queues section.
        </Alert>
      )}
      <ValkyrieLambdaListEditor
        lambdas={items}
        defaults={defaults}
        showDefaults={false}
        queueNames={queueNames}
        onChange={commit}
      />
    </>
  );
}
